// R54 — HER SAVED VERSES: THE CHIP SAID "✓ SAVED" BEFORE THE REQUEST LEFT, AND NEVER TOOK IT BACK.
//
// THE HUNT is the pointer's, one route on from R51/R52/R53. `/api/verses/saved` has THREE call sites
// and this is the sharpest instance of the shape yet, because **TWO OF THE THREE PAINT HER SCREEN
// BEFORE THE REQUEST IS SENT** and neither reads what comes back.
//
//   * **THE SAVE.** `setSaved(true)` first, then `await fetch(...).catch(() => setSaved(false))`.
//     **A `.catch` FIRES ON A THROWN REQUEST AND ON NOTHING ELSE.** The route refuses FOUR ways and
//     every one of them RESOLVES: 401 "Sign in to save verses."; **HTTP 200 `{ ok: false }`** when
//     `ensureUser` comes back empty (R39/R47/R51/R53's shape, a FIFTH route); 200 `{ ok: false,
//     message: "Nothing to save." }`; and 500. So the chip reads **"✓ Saved"** and goes disabled,
//     and the verse is nowhere. She finds out by opening My Room and not finding it.
//   * **THE DELETE.** The verse is filtered off her screen FIRST, then `await fetch(DELETE)
//     .catch(() => {})` — the answer is not read at all. **AND THIS ONE IS R35's FINDING WITH THE
//     SIDES REVERSED: the server already tells the truth here** (`sendJson(response, 200, { ok:
//     await deleteVerse(userId, id) })` — a real boolean, whether a row went) and the client throws
//     it away. A delete that did not happen leaves her screen and her room disagreeing until she
//     reloads, at which point the verse is back and nothing ever said why.
//   * **THE READ.** `r.ok ? r.json() : { verses: [] }` then `setSavedVerses([])` — **a failed read
//     BLANKS her saved verses** (R18, and R53's finding one route over).
//
// R35 ruled this delete right NOT to ask before it runs (`NO_ASK_BY_DESIGN`: "unpins a verse; the
// verse itself is untouched and the pin is one tap to restore"), and that ruling stands — this round
// is not a confirm. **AN OPTIMISTIC PAINT IS ALSO FINE, AND IS KEPT: what was missing is the PUT
// BACK.** A screen that is ahead of the database is a nice feeling; a screen that STAYS wrong is a
// lie, and the difference between them is entirely in what happens when the answer says no.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law), and the answer
// shape is deliberately `room-state.ts` / `people.ts` / `saved-reports.ts`'s rather than a fifth key.

export const savedVersesPath = "/api/verses/saved";
export const savedVersePath = (id: number | string) => `${savedVersesPath}/${encodeURIComponent(String(id))}`;

// The row as My Room draws it. `topic` is what she searched to find it and rides along for nothing
// more than that.
export type SavedVerse = { id: number; reference: string; verseText: string; source: string; topic?: string };

// FOUR answers, and the two in the middle are the ones a status code cannot tell apart.
//   sign-in — the session is gone (401). Nothing of hers is lost; she signs in and it saves.
//   refused — the server DELIBERATELY said no, at 200 or at 4xx/5xx, and it usually said why.
//   offline — the request never landed. Nothing was decided; transport noise (R18).
export type VerseAnswer =
  | { ok: true }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

// The two sentences the house owns, handed in by whoever holds the string catalog — this module
// imports nothing and cannot read it (`saved-books.ts`'s fallback-parameter shape). `failed` differs
// between saving and unpinning, so it is a parameter rather than a second law.
export type VerseCopy = { signIn: string; failed: string };

type Json = { ok?: boolean; message?: string };

// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate drives every answer the route can
// give without a browser, a session or a database.
async function ask(path: string, init: RequestInit, doFetch: typeof fetch): Promise<VerseAnswer> {
  try {
    const res = await doFetch(path, init);
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = (await res.json().catch(() => ({}))) as Json;
    // **`ok` IS READ, NOT THE STATUS CODE.** Every refusal this route composes except the 401 and
    // the 500 arrives at HTTP 200, which is precisely why a `.catch` saw none of them.
    if (res.ok && data?.ok !== false) return { ok: true };
    return { ok: false, kind: "refused", message: typeof data?.message === "string" ? data.message : "" };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// THE READ. **A FAILED READ IS NOT AN EMPTY COLLECTION** (R18): null, so the panel keeps whatever it
// was already showing rather than blanking her verses because a request lost its way. The route's
// own `ensureUser` early return sends a real `{ verses: [] }` — an account with no rows — so the two
// are told apart by the SHAPE, not by the status code.
export async function listSavedVerses(doFetch: typeof fetch = fetch): Promise<SavedVerse[] | null> {
  try {
    const res = await doFetch(savedVersesPath, { cache: "no-store" });
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as { verses?: unknown } | null;
    return Array.isArray(data?.verses) ? (data.verses as SavedVerse[]) : null;
  } catch {
    return null;
  }
}

export function saveVerse(
  verse: { reference: string; verseText: string; source: string; topic?: string },
  doFetch: typeof fetch = fetch,
): Promise<VerseAnswer> {
  return ask(savedVersesPath, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(verse),
  }, doFetch);
}

// The DELETE's answer is a real boolean the server computed, and it is the one this round exists to
// start reading. A refusal is what puts the row back.
export function deleteSavedVerse(id: number | string, doFetch: typeof fetch = fetch): Promise<VerseAnswer> {
  return ask(savedVersePath(id), { method: "DELETE" }, doFetch);
}

// The sentence, derived from the answer rather than typed per surface. A success says NOTHING: the
// chip turning, or the row leaving, IS the answer (R51/R53's ruling on the same question).
export function verseNote(answer: VerseAnswer, copy: VerseCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}
